/**
 * ============================================================
 * MONTE HERMOM | PERFIL CARD SOBERANO
 * ============================================================
 *
 * ARQUIVO: PerfilCard.jsx
 * VERSÃO: 1.1.0
 * DATA: 18/02/2026
 *
 * OBJETIVO:
 *  - Exibir dados do usuário autenticado
 *  - Exibir APENAS o selo de segurança mais alto
 *  - Destacar visualmente evolução de segurança (upgrade)
 *  - Harmonizar com MenuParadigmaLateral
 *
 * DEPENDÊNCIA:
 *  - JSON retornado por /api/auth/check_session.php
 *
 * ESTRUTURA ESPERADA:
 *  user: {
 *    nome,
 *    email,
 *    ambiente,
 *    seguranca: {
 *      nome,
 *      ordem,
 *      img
 *    }
 *  }
 * ============================================================
 */

import React, { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';

export default function PerfilCard({ user }) {
  if (!user) return null;

  const seguranca = user.seguranca || null;
  const ordemAtual = seguranca?.ordem ?? 0;

  /* ============================================================
     CONTROLE DE EVOLUÇÃO DE SEGURANÇA (ITEM 10)
     ============================================================ */
  const [upgrade, setUpgrade] = useState(false);

  useEffect(() => {
    const ordemAnterior = parseInt(
      localStorage.getItem('mh_nivel_seguranca') || '0',
      10
    );

    if (ordemAtual > ordemAnterior) {
      setUpgrade(true);

      // remove destaque após alguns segundos
      const timer = setTimeout(() => {
        setUpgrade(false);
      }, 4000);

      return () => clearTimeout(timer);
    }

    localStorage.setItem('mh_nivel_seguranca', ordemAtual);
  }, [ordemAtual]);

  // garante persistência do nível atual
  useEffect(() => {
    localStorage.setItem('mh_nivel_seguranca', ordemAtual);
  }, [ordemAtual]);

  return (
    <div className="bg-[#0A0A0A] border border-white/5 rounded-[2.5rem] p-6 flex items-center gap-5 shadow-xl">
      
      {/* AVATAR */}
      <div className="w-14 h-14 rounded-full bg-slate-800 border-2 border-white/10 flex items-center justify-center text-white font-black">
        {user.nome?.charAt(0)}
      </div>

      {/* INFO USUÁRIO */}
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-black uppercase tracking-tight text-white truncate">
          {user.nome}
        </p>
        <p className="text-[9px] text-slate-400 truncate">
          {user.email}
        </p>

        {/* AMBIENTE */}
        <span className="inline-block mt-1 px-2 py-[2px] text-[8px] font-black uppercase tracking-widest rounded-full
          bg-white/5 text-white/60">
          {user.ambiente === 'MH' ? 'Monte Hermom' : 'SaaS'}
        </span>
      </div>

      {/* SELO DE SEGURANÇA */}
      <div
        className={`flex flex-col items-center gap-1 transition-all duration-700
          ${upgrade ? 'scale-110 drop-shadow-[0_0_12px_rgba(255,215,0,0.6)]' : ''}
        `}
      >
        {seguranca?.img ? (
          <>
            <img
              src={seguranca.img}
              alt={seguranca.nome}
              title={seguranca.nome}
              className="w-10 h-10 object-contain"
            />
            <span className="text-[7px] text-white/50 uppercase tracking-widest font-black text-center">
              {seguranca.nome}
            </span>
          </>
        ) : (
          <>
            <ShieldCheck size={22} className="text-white/20" />
            <span className="text-[7px] text-white/30 uppercase tracking-widest font-black text-center">
              Básico
            </span>
          </>
        )}
      </div>

    </div>
  );
}